/**
 * 错误前后业务语义环形缓冲区（P1-6）
 *
 * SDK 自带的 breadcrumb 记录 DOM/HTTP 操作序列（见 src/config/apm.ts），本模块记录的是
 * 「业务语义」现场（画布保存/加载阶段变化、节点规模变化等），与 breadcrumb 互补，
 * 同样只写内存、零常态上报——只有错误发生时才随 dumpErrorDetail 一并携带（见 snapshot.ts）。
 *
 * 真正的差异化价值在「崩溃后」：真·标签页崩溃时 JS 无机会执行任何上报代码
 * （docs/20260819_observability_tech_report.md 已论证捕获率 ≈0），breadcrumb 存在 SDK
 * 内存里也会随进程一起消失。故本模块定期把窗口快照写 sessionStorage（覆盖写，不上报），
 * 下次启动时若发现「上次未正常退出」，才补报这份崩溃前的业务现场——这是「崩溃前后」
 * 场景里唯一可行的实现方式：崩溃前的现场靠快照留存，崩溃事实本身则由
 * gpuMemoryMonitor.ts 的 localStorage 心跳独立反推（两套机制互不依赖，互为补充）。
 */

import { apmClient } from '@/config/apm';

type Frame = { t: number; kind: string; msg: string };

const CAP = 100; // 最近 100 条
const WINDOW_MS = 60_000; // 或最近 60 秒，二者取小
const SNAP_KEY = 'amai_crash_window';
const SNAP_INTERVAL_MS = 10_000;

const ring: Frame[] = [];

/** 常态调用：只写内存环形缓冲区，不产生任何上报 */
export function mark(kind: string, msg: string): void {
  ring.push({ t: Date.now(), kind, msg: msg.slice(0, 120) });
  if (ring.length > CAP) ring.shift();
}

/** 供 dumpErrorDetail 等错误路径调用：取最近 60 秒内的业务语义现场 */
export function recentFrames(): string[] {
  const since = Date.now() - WINDOW_MS;
  return ring.filter(f => f.t >= since).map(f => `${f.t}|${f.kind}|${f.msg}`);
}

let snapTimer: ReturnType<typeof setInterval> | null = null;

/** 应用启动时调用一次：开启定期快照（供崩溃后补报）+ 正常退出清理 */
export function startCrashWindowSnapshot(): void {
  if (snapTimer !== null) return;
  snapTimer = setInterval(() => {
    try {
      sessionStorage.setItem(SNAP_KEY, JSON.stringify(recentFrames()));
    } catch {
      // sessionStorage 不可用或超额，不影响主流程
    }
  }, SNAP_INTERVAL_MS);

  // 正常退出：清掉快照，下次启动即判定「上次是正常退出」
  window.addEventListener('pagehide', () => {
    try {
      sessionStorage.removeItem(SNAP_KEY);
    } catch {
      // 忽略
    }
  });
}

/**
 * 启动时调用一次：若上次会话存在未清理的快照（意味着上次未走到 pagehide 就终止了），
 * 补报这份崩溃前 60 秒的业务现场。仅产生一条日志，供人工排查用，不重复 crash_confirmed
 * 事件本身（那由 gpuMemoryMonitor.ts 独立判定并上报）。
 */
export function reportPreviousCrashWindow(): void {
  let snap: string | null = null;
  try {
    snap = sessionStorage.getItem(SNAP_KEY);
  } catch {
    return;
  }
  if (!snap) return; // 上次是正常退出

  try {
    apmClient.sendLog?.({
      level: 'error',
      content: `crash_window:${snap}`.slice(0, 1024),
      extra: { trigger: 'suspected_crash_context' },
    });
  } catch {
    // 忽略
  } finally {
    try {
      sessionStorage.removeItem(SNAP_KEY);
    } catch {
      // 忽略
    }
  }
}
